import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { getRsvpInfo, submitRsvp, createTicketCheckout } from "../lib/api";
import { useAuth } from "../lib/auth";
import { X, Loader2, Ticket, Users } from "lucide-react";

const inputCls = "w-full rounded-md border border-black/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#6B21A8]";

export default function RsvpModal({ event, onClose }) {
  const { user } = useAuth();
  const [f, setF] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    quantity: 1,
  });
  const [loading, setLoading] = useState(false);

  const { data: info } = useQuery({
    queryKey: ["rsvp-info", event.id],
    queryFn: () => getRsvpInfo(event.id),
  });

  useEffect(() => {
    if (user) setF((s) => ({ ...s, name: s.name || user.name, email: s.email || user.email }));
  }, [user]);

  const set = (k) => (e) => setF({ ...f, [k]: k === "quantity" ? Number(e.target.value) : e.target.value });

  const isPaid = (info?.ticket_price || 0) > 0;
  const total = isPaid ? (info.ticket_price * f.quantity).toFixed(2) : null;
  const fullyBooked = info?.remaining === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (fullyBooked) return toast.error("This event is at capacity.");
    setLoading(true);
    try {
      if (isPaid) {
        const { checkout_url } = await createTicketCheckout(event.id, {
          name: f.name, email: f.email, phone: f.phone, quantity: f.quantity,
          origin_url: window.location.origin,
        });
        window.location.href = checkout_url;
      } else {
        await submitRsvp(event.id, { name: f.name, email: f.email, phone: f.phone, quantity: f.quantity });
        toast.success("You're on the list — see you there.");
        onClose();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="rsvp-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white border-2 border-black shadow-2xl">
        <div className="flex items-center justify-between border-b border-black/10 bg-[#0A0A0A] text-white px-5 py-3">
          <div>
            <p className="overline text-[#F5B90A]">{isPaid ? "Get your ticket" : "RSVP"}</p>
            <p className="font-display text-lg font-semibold leading-tight">{event.title}</p>
          </div>
          <button onClick={onClose} data-testid="rsvp-close" aria-label="Close" className="p-1 rounded hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4" data-testid="rsvp-form">
          <div className="flex items-center gap-3 text-sm text-black/70">
            {isPaid ? <Ticket className="h-5 w-5 text-[#6B21A8]" /> : <Users className="h-5 w-5 text-[#6B21A8]" />}
            <span>
              {isPaid
                ? <>Tickets are <strong>${info.ticket_price?.toFixed(2)}</strong> each.</>
                : <>Free RSVP — save your spot.</>}
              {info?.capacity != null && (
                <span className="ml-2 text-xs text-black/50">
                  ({info.remaining ?? "—"} of {info.capacity} left)
                </span>
              )}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-xs font-medium text-black/70">Name *</span><input required className={inputCls + " mt-1"} value={f.name} onChange={set("name")} data-testid="rsvp-name" /></label>
            <label className="block"><span className="text-xs font-medium text-black/70">Email *</span><input required type="email" className={inputCls + " mt-1"} value={f.email} onChange={set("email")} data-testid="rsvp-email" /></label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-xs font-medium text-black/70">Phone</span><input className={inputCls + " mt-1"} value={f.phone} onChange={set("phone")} data-testid="rsvp-phone" /></label>
            <label className="block">
              <span className="text-xs font-medium text-black/70">Quantity</span>
              <input type="number" min="1" max="20" className={inputCls + " mt-1"} value={f.quantity} onChange={set("quantity")} data-testid="rsvp-quantity" />
            </label>
          </div>

          {isPaid && (
            <div className="border border-[#6B21A8] bg-[#6B21A8]/5 p-3 flex items-baseline justify-between">
              <span className="text-sm text-black/70">Total</span>
              <span className="font-display text-2xl font-bold text-[#6B21A8]">${total}</span>
            </div>
          )}

          <button type="submit" disabled={loading || fullyBooked} className="btn-primary w-full" data-testid="rsvp-submit">
            {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {isPaid ? "Redirecting…" : "Sending…"}</>) : (isPaid ? "Continue to secure checkout" : "Confirm RSVP")}
          </button>
          {fullyBooked && <p className="text-xs text-red-600 text-center">This event is at capacity.</p>}
          {isPaid && <p className="text-xs text-black/50 text-center">Secure checkout via Stripe. Your ticket will be emailed after payment.</p>}
        </form>
      </div>
    </div>
  );
}
